// Main Layout component with sidebar navigation
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Calendar, 
  ClipboardCheck, 
  Users, 
  Trophy, 
  GraduationCap,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Globe,
  UserCircle,
  BookOpen
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isSeniorFacilitator } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['admin', 'senior_facilitator', 'facilitator'] },
    { path: '/events', icon: Calendar, label: t('events'), roles: ['admin', 'senior_facilitator', 'facilitator'] },
    { path: '/evaluations', icon: ClipboardCheck, label: t('evaluations'), roles: ['admin', 'senior_facilitator', 'facilitator'] },
    { path: '/leaderboard', icon: Trophy, label: t('leaderboard'), roles: ['admin', 'senior_facilitator', 'facilitator'] },
    { path: '/training-requests', icon: GraduationCap, label: language === 'el' ? 'Εκπαίδευση' : 'Training', roles: ['admin', 'senior_facilitator', 'facilitator'] },
    { path: '/admin', icon: Settings, label: t('adminPanel'), roles: ['admin'] },
    { path: '/guide', icon: BookOpen, label: language === 'el' ? 'Οδηγός' : 'Guide', roles: ['admin', 'senior_facilitator', 'facilitator'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'bg-purple-100 text-purple-700 border-purple-200',
      senior_facilitator: 'bg-blue-100 text-blue-700 border-blue-200',
      facilitator: 'bg-green-100 text-green-700 border-green-200'
    };
    const roleLabels = {
      admin: t('admin'),
      senior_facilitator: t('seniorFacilitator'),
      facilitator: t('facilitator')
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full border ${roleColors[role]}`}>
        {roleLabels[role]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#E6007E] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">G</span>
            </div>
            <span className="font-heading font-bold text-[#2A1514]">GrowPlay</span>
          </div>
          <button
            onClick={toggleLanguage}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="language-toggle-mobile"
          >
            <Globe size={20} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E6007E] rounded-xl flex items-center justify-center shadow-lg shadow-[#E6007E]/20">
                <span className="text-white font-bold text-lg">G</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-[#2A1514] text-lg leading-tight">GrowPlay</h1>
                <p className="text-xs text-gray-500">Facilitator Hub</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto min-h-0">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl
                    transition-all duration-200 group
                    ${isActive 
                      ? 'bg-[#E6007E] text-white shadow-lg shadow-[#E6007E]/20' 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon size={20} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#E6007E]'} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            {/* Language Toggle — pinned in sidebar */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-100 transition-all duration-200 w-full"
              data-testid="language-toggle"
            >
              <Globe size={20} className="text-gray-400" />
              <span className="font-medium">{language === 'el' ? 'English' : 'Ελληνικά'}</span>
            </button>
          </nav>

          {/* User Section */}
          <div className="p-3 lg:p-4 border-t border-gray-100 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#E6007E] text-white">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-[#2A1514] truncate">{user?.name}</p>
                    {getRoleBadge(user?.role)}
                  </div>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                  <UserCircle className="mr-2 h-4 w-4" />
                  {t('profile')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0 overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
